import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, Plus, Calendar, Image as ImageIcon, FileText, X, Pencil, CheckCircle2, AlertCircle } from 'lucide-react';
import RichTextEditor from '../../components/ui/RichTextEditor';
import { getImageUrl, getBackendUrl, translateAndSlugify } from '../../lib/utils';

const formatToTurkishDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const parseTurkishDateToISO = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  
  const months = {
    ocak: 0, subat: 1, şubat: 1, mart: 2, nisan: 3, mayis: 4, mayıs: 4, haziran: 5, temmuz: 6,
    agustos: 7, ağustos: 7, eylul: 8, eylül: 8, ekim: 9, kasim: 10, kasım: 10, aralik: 11, aralık: 11
  };
  const parts = dateStr.toLowerCase().split(' ');
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const monthName = parts[1].replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ç/g, 'c');
    const month = months[monthName];
    const year = parseInt(parts[2], 10);
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      const date = new Date(year, month, day);
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
  }
  
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ 
    title: '', 
    slug: '',
    summary: '', 
    content: '', 
    date: '', 
    image: '',
    seo_title: '', 
    seo_description: '' 
  });
  const [editingId, setEditingId] = useState(null);
  const [file, setFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [existingGallery, setExistingGallery] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');



  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const url = getBackendUrl();
      const { data } = await axios.get(`${url}/api/news`);
      setNews(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEditClick = (item) => {
    const itemId = item.id || item._id;
    setEditingId(itemId);
    setFormData({
      title: item.title || '',
      slug: item.slug || '',
      summary: item.summary || '',
      content: item.content || '',
      date: item.date || '',
      image: item.image || '',
      seo_title: item.seo_title || '',
      seo_description: item.seo_description || ''
    });
    
    let parsedGallery = [];
    try {
      if (item.gallery) {
        parsedGallery = typeof item.gallery === 'string' ? JSON.parse(item.gallery) : item.gallery;
      }
    } catch (e) {
      parsedGallery = [];
    }
    setExistingGallery(Array.isArray(parsedGallery) ? parsedGallery : []);
    setGalleryFiles([]);
    
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData({ 
      title: '', 
      slug: '',
      summary: '', 
      content: '', 
      date: '', 
      image: '',
      seo_title: '', 
      seo_description: '' 
    });
    setFile(null);
    setGalleryFiles([]);
    setExistingGallery([]);
  };

  const handleNewClick = () => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    setFormData({
      title: '',
      slug: '',
      summary: '',
      content: '',
      date: formattedDate,
      image: '',
      seo_title: '',
      seo_description: ''
    });
    setEditingId(null);
    setFile(null);
    setGalleryFiles([]);
    setExistingGallery([]);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const data = new FormData();
    data.append('title', formData.title);
    data.append('slug', formData.slug);
    data.append('summary', formData.summary);
    data.append('content', formData.content);
    data.append('date', formData.date || new Date().toLocaleDateString('tr-TR'));
    data.append('seo_title', formData.seo_title);
    data.append('seo_description', formData.seo_description);
    data.append('existing_image', formData.image);
    data.append('existing_gallery', JSON.stringify(existingGallery));
    
    galleryFiles.forEach((f) => {
      data.append('gallery_images[]', f);
    });
    
    if (editingId) {
      data.append('id', editingId);
    }
    if (file) data.append('image', file);

    try {
      const url = getBackendUrl();
      await axios.post(`${url}/api/news`, data, { 
         headers: { 'Content-Type': 'multipart/form-data' },
         withCredentials: true 
      });
      handleCloseModal();
      fetchNews();
      setSuccess("Haber başarıyla kaydedildi!");
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || err.message;
      setError("Haber kaydedilirken bir hata oluştu: " + errMsg);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu haberi silmek istediğinize emin misiniz?")) return;
    try {
      const url = getBackendUrl();
      await axios.delete(`${url}/api/news/${id}`, { withCredentials: true });
      fetchNews();
      setSuccess("Haber başarıyla silindi!");
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError("Haber silinirken bir hata oluştu.");
      setTimeout(() => setError(''), 5000);
    }
  };

  const renderLengthBar = (length, min, max) => {
    const percentage = Math.min((length / max) * 100, 100);
    let barColor = "bg-amber-400"; // Çok kısa / Uzun değil
    if (length >= min && length <= max) {
      barColor = "bg-green-500"; // Mükemmel
    } else if (length > max) {
      barColor = "bg-red-500"; // Çok uzun
    }
    return (
      <div className="w-full h-1 bg-slate-200 rounded-full mt-1.5 overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-300`} 
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  const getPreviewTitle = () => {
    return formData.seo_title && formData.seo_title.trim() !== '' 
      ? formData.seo_title 
      : (formData.title || 'Arama Başlığı');
  };

  const getPreviewDesc = () => {
    const raw = formData.seo_description && formData.seo_description.trim() !== '' 
      ? formData.seo_description 
      : (formData.summary || '');
    if (raw.length > 160) {
      return raw.substring(0, 160) + '...';
    }
    return raw || 'Arama sonucu sayfa açıklaması burada görüntülenecektir...';
  };

  const effectiveTitleLength = formData.seo_title && formData.seo_title.trim() !== '' 
    ? formData.seo_title.length 
    : (formData.title ? formData.title.length : 0);

  const effectiveDescLength = formData.seo_description && formData.seo_description.trim() !== '' 
    ? formData.seo_description.length 
    : (formData.summary ? formData.summary.length : 0);

  return (
    <div className="space-y-6">
      {/* Toast Alert */}
      {(success || error) && (
        <div className="fixed top-24 right-8 z-50 animate-bounce">
          {success && (
            <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
              <CheckCircle2 size={24} className="text-emerald-500" />
              <span className="font-bold text-sm">{success}</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 text-red-800 border border-red-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
              <AlertCircle size={24} className="text-red-500" />
              <span className="font-bold text-sm">{error}</span>
            </div>
          )}
        </div>
      )}
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Haberler ve Duyurular</h2>
          <p className="text-xs text-slate-500 mt-1">Web sitenizde yayınlanan tüm haberleri buradan yönetebilirsiniz.</p>
        </div>
        <button 
          onClick={handleNewClick} 
          className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-600/10 transition-all text-sm"
        >
          <Plus size={18} /> Yeni Haber Ekle
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {news.map(item => {
          const itemId = item.id || item._id;
          return (
            <div key={itemId} className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden flex flex-col group hover:shadow-md transition-all duration-300">
              <div className="h-48 bg-slate-100 relative overflow-hidden">
                {item.image ? (
                  <img src={getImageUrl(item.image)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={item.title} />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-2 bg-slate-50">
                    <ImageIcon size={32} strokeWidth={1.5} />
                    <span className="text-xs font-semibold">Görsel Yok</span>
                  </div>
                )}
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-3 py-1.5 rounded-xl text-xs font-bold text-slate-700 flex items-center gap-2 shadow-sm">
                  <Calendar size={14} className="text-blue-600" />
                  {item.date}
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-bold text-slate-900 text-lg mb-2 line-clamp-2 leading-snug group-hover:text-blue-600 transition-colors">{item.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-3 mb-6 flex-1 leading-relaxed">{item.summary}</p>
                <div className="flex gap-2 w-full mt-auto">
                  <button 
                    onClick={() => handleEditClick(item)} 
                    className="flex items-center justify-center gap-2 flex-1 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl text-xs font-bold transition-all"
                  >
                    <Pencil size={14} /> Düzenle
                  </button>
                  <button 
                    onClick={() => handleDelete(itemId)} 
                    className="flex items-center justify-center gap-2 flex-1 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-bold transition-all"
                  >
                    <Trash2 size={14} /> Sil
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-[1600px] max-h-[90vh] overflow-y-auto p-6 md:p-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900">{editingId ? 'Haberi Düzenle' : 'Yeni Haber Ekle'}</h2>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-50 rounded-xl transition">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Sol Panel: İçerik Yönetimi */}
                <div className="lg:col-span-8 space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Başlık</label>
                    <input 
                      required 
                      type="text" 
                      value={formData.title} 
                      onChange={e => setFormData({...formData, title: e.target.value})} 
                      onBlur={async () => {
                        if (!formData.slug && formData.title) {
                          const ts = await translateAndSlugify(formData.title);
                          setFormData(prev => ({ ...prev, slug: ts }));
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Tarih</label>
                      <input 
                        required 
                        type="date" 
                        value={parseTurkishDateToISO(formData.date)} 
                        onChange={e => {
                          const val = e.target.value;
                          setFormData({
                            ...formData,
                            date: formatToTurkishDate(val)
                          });
                        }} 
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Kapak Görseli</label>
                      <div className="flex items-center gap-3">
                        {file ? (
                          <div className="relative w-16 h-12 rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-slate-50 flex-shrink-0 group">
                            <img src={URL.createObjectURL(file)} className="w-full h-full object-cover" alt="Önizleme" />
                            <button 
                              type="button" 
                              onClick={() => setFile(null)} 
                              className="absolute inset-0 bg-red-600/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                              title="Seçimi Kaldır"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : formData.image ? (
                          <div className="relative w-16 h-12 rounded-lg overflow-hidden border border-slate-200 shadow-sm bg-slate-50 flex-shrink-0 group">
                            <img src={getImageUrl(formData.image)} className="w-full h-full object-cover" alt="Mevcut Kapak" />
                            <button 
                              type="button" 
                              onClick={() => setFormData({ ...formData, image: '' })}
                              className="absolute inset-0 bg-red-600/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                              title="Kapak Görselini Kaldır"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="w-16 h-12 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-[9px] font-semibold bg-slate-50 flex-shrink-0">
                            Görsel Yok
                          </div>
                        )}
                        <input type="file" onChange={e => {
                          if (e.target.files && e.target.files[0]) {
                            setFile(e.target.files[0]);
                          }
                        }} className="w-full p-2 border border-slate-200 rounded-xl bg-slate-50 text-xs cursor-pointer font-semibold file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" accept="image/*" />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50/50 p-5 rounded-2xl border border-slate-200/60 space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Haber Galerisi Görselleri (Çoklu Seçim)</label>
                      <input type="file" multiple onChange={e => setGalleryFiles(Array.from(e.target.files))} className="w-full p-2 border border-slate-200 rounded-xl bg-white text-xs cursor-pointer font-semibold file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" accept="image/*" />
                      <p className="text-[10px] text-slate-400 mt-1">Birden fazla görsel seçerek yükleyebilirsiniz.</p>
                    </div>

                    {/* Newly Selected Gallery Previews */}
                    {galleryFiles.length > 0 && (
                      <div>
                        <label className="block text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-2">Yüklenecek Yeni Görseller ({galleryFiles.length} adet)</label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                          {galleryFiles.map((f, i) => (
                            <div key={i} className="relative aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden border border-blue-200 group">
                              <img src={URL.createObjectURL(f)} className="w-full h-full object-cover" alt="Önizleme" />
                              <button 
                                type="button" 
                                onClick={() => setGalleryFiles(galleryFiles.filter((_, idx) => idx !== i))}
                                className="absolute inset-0 bg-red-600/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                title="Seçimi Kaldır"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {existingGallery.length > 0 && (
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Kayıtlı Galeri Görselleri (Silmek için üzerine tıklayın)</label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
                          {existingGallery.map((imgUrl, i) => (
                            <div key={i} className="relative aspect-[4/3] bg-slate-100 rounded-lg overflow-hidden border border-slate-200 group">
                              <img src={getImageUrl(imgUrl)} className="w-full h-full object-cover" alt="Galeri" />
                              <button 
                                type="button" 
                                onClick={() => setExistingGallery(existingGallery.filter((_, idx) => idx !== i))}
                                className="absolute inset-0 bg-red-600/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                title="Görseli Sil"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Özet</label>
                    <textarea required rows={3} value={formData.summary} onChange={e => setFormData({...formData, summary: e.target.value})} className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none resize-none"></textarea>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Detaylı İçerik (HTML destekli)</label>
                    <RichTextEditor value={formData.content} onChange={html => setFormData({...formData, content: html})} />
                  </div>
                </div>

                {/* Sağ Panel: SEO ve URL Yapılandırması */}
                <div className="lg:col-span-4 space-y-6">
                  <div className="bg-slate-50/70 p-6 rounded-3xl border border-slate-200/60 space-y-5">
                    <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2 border-b border-slate-200 pb-3">
                      <FileText size={18} className="text-blue-600" /> SEO ve URL Yapılandırması
                    </h3>
                    
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">URL Takısı (Slug)</label>
                      <input type="text" value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} placeholder="Örn: ss-ikitelli-silivri-toreni" className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" />
                      <p className="text-[10px] text-slate-400 mt-1">Boş bırakırsanız başlığa göre otomatik oluşturulur.</p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">SEO Başlığı</label>
                      <input type="text" value={formData.seo_title} onChange={e => setFormData({...formData, seo_title: e.target.value})} placeholder="Boş bırakılırsa haber başlığı kullanılır" className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" />
                      {renderLengthBar(effectiveTitleLength, 40, 60)}
                      <p className="text-[10px] text-slate-400 mt-1 flex justify-between">
                        <span>Önerilen: 40-60 karakter</span>
                        <span>{effectiveTitleLength} karakter</span>
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">SEO Açıklaması</label>
                      <textarea rows={4} value={formData.seo_description} onChange={e => setFormData({...formData, seo_description: e.target.value})} placeholder="Boş bırakılırsa haber özeti kullanılır" className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none resize-none"></textarea>
                      {renderLengthBar(effectiveDescLength, 120, 160)}
                      <p className="text-[10px] text-slate-400 mt-1 flex justify-between">
                        <span>Önerilen: 120-160 karakter</span>
                        <span>{effectiveDescLength} karakter</span>
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Arama Motoru Önizlemesi</label>
                      <div className="bg-white p-4 rounded-2xl border border-slate-200 text-xs space-y-1.5 shadow-inner">
                        <div className="text-[10px] text-emerald-700 truncate font-mono">http://localhost:3000/haberler/{formData.slug || 'haber-detay'}</div>
                        <div className="text-blue-700 font-bold text-sm truncate hover:underline cursor-pointer">
                          {getPreviewTitle()}
                        </div>
                        <div className="text-slate-500 line-clamp-2 leading-relaxed">
                          {getPreviewDesc()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                <button type="button" onClick={handleCloseModal} className="px-6 py-3 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold text-xs transition">İptal</button>
                <button type="submit" disabled={loading} className="px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 rounded-xl font-bold text-xs shadow-lg shadow-blue-600/10 transition disabled:opacity-50">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews;

